import React, { Dispatch, SetStateAction, useEffect } from 'react';
import { FaExclamationCircle } from 'react-icons/fa';
import Slider from 'rc-slider';
import TextareaAutosize from 'react-textarea-autosize';

import { UseOutletContextProduct } from '../Product';
import { StateLanguage } from '../../../../../languages/config/StateLanguage';
import { Container } from '../../../../../styles/container.style';
import { uploadTooltip } from '../../../../../utils/tooltip.util';
import { legendInvalid, legendValid } from '../../../../../utils/legend.data.util';
import { evaluateLegendObjectOptionalIcon, evaluateLegendObjectOptional, evaluateLegendObjectRequiredIcon, evaluateLegendObjectCompositeRequiredIcon } from '../../../../../scripts/validate.legend.script';
import { expressions } from '../../../../../libraries/regular.expressions.library';
import { uploadImg } from '../../../../../scripts/file.load.script';
import { prefixLegend } from '../../../../../scripts/name.legend.script';

import AppLegend from '../../../../../components/element/Legend';
import AppInputTaxFee from '../../../../../components/element/InputTaxFee';
import AppInputMeasureUnit from '../../../../../components/element/InputMeasureUnit';
import AppInputFeatureOptions from '../../../../../components/element/InputFeatureOptions';
import imgDefault from '../../../../../assets/themes/default.png';

export interface AppProductVariantFormProps {
  component?: string,
  variant: {value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}},
  setVariant: Dispatch<SetStateAction<{value: {id: string, description_short: string, description_long: string, code: string, sku: string, codebar: string, price_sale: string, price_purchase: string, tax_code_sale: string, tax_code_purchase: string, tax_rate_sale: number, tax_rate_purchase: number, unit: string, discount: {type: string, value: string, quantity: string}, photos: {name: string, size: number, type: string, view: string, file: any}[], features: {id_feature: string, name: string, value: string}[]}, valid: {description_short: boolean, description_long: boolean, code: boolean, sku: boolean, codebar: boolean, price_sale: boolean, price_purchase: boolean, tax_sale: boolean, tax_purchase: boolean, unit: boolean, discount: {type: boolean, value: boolean, quantity: boolean}, photos: {file: boolean, message: string}[], features: boolean[], gallery: boolean}}>>
};

const AppProductVariantForm: React.FunctionComponent<AppProductVariantFormProps> = ({component, variant, setVariant}) => {
  const {companyForUser, settingsForCompany, loadSettingsForCompany} = UseOutletContextProduct()
  const {lang} = StateLanguage()

  const handleChangeFiles = async (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
    let archiveValueList: {name: string, size: number, type: string, view: string, file: any}[] = []
    let archiveValidList: {file: boolean, message: string}[] = []
    let archivesCapture: any = event.target.files

    for (let item of archivesCapture) {
      let archive = await uploadImg(lang, item)
      archiveValueList.push(archive.value)
      archiveValidList.push(archive.valid)
    }

    let tempPhotosValue = [...variant.value.photos, ...archiveValueList]
    let tempPhotosValid = [...variant.valid.photos, ...archiveValidList]
    let tempGalleryValid = (tempPhotosValid.length > 10) ? false : true

    if (tempGalleryValid) {
      legendValid(`${prefixLegend(component)}legend-variant-gallery-max`)
    } else {
      legendInvalid(`${prefixLegend(component)}legend-variant-gallery-max`)
    }

    setVariant({value: {...variant.value, photos: tempPhotosValue}, valid: {...variant.valid, photos: tempPhotosValid, gallery: tempGalleryValid}})
    cleanInputFile()
  }

  const handleChangeDescriptionShortToVariant = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setVariant({...variant, value: {...variant.value, description_short: event.target.value}})
  }

  const handleChangeDescriptionLongToVariant = (event: React.ChangeEvent <HTMLFormElement | HTMLTextAreaElement>) => {
    setVariant({...variant, value: {...variant.value, description_long: event.target.value}})
  }

  const handleChangeCodeToVariant = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setVariant({...variant, value: {...variant.value, code: event.target.value}})
  }

  const handleChangeSkuToVariant = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setVariant({...variant, value: {...variant.value, sku: event.target.value}})
  }

  const handleChangeCodebarToVariant = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setVariant({...variant, value: {...variant.value, codebar: event.target.value}})
  }

  const handleChangePriceSaleToVariant = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setVariant({...variant, value: {...variant.value, price_sale: event.target.value}})
  }

  const handleChangePricePurchaseToVariant = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setVariant({...variant, value: {...variant.value, price_purchase: event.target.value}})
  }

  const handleChangeDiscountTypeToVariant = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setVariant({value: {...variant.value, discount: {type: event.target.value, value: (event.target.value === 'percentage') ? '10' : '', quantity: ''}}, valid: {...variant.valid, discount: {type: true, value: (event.target.value === 'none'), quantity: (event.target.value === 'none')}}})
  }

  const handleChangeInputDiscountValueToVariant = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setVariant({...variant, value: {...variant.value, discount: {...variant.value.discount, value: event.target.value}}})
  }

  const handleChangeInputDiscountQuantityToVariant = (event: React.ChangeEvent <HTMLFormElement | HTMLInputElement>) => {
    setVariant({...variant, value: {...variant.value, discount: {...variant.value.discount, quantity: event.target.value}}})
  }

  const handleChangeSlideDiscountValueToVariant = (item: number | number[]) => {
    setVariant({value: {...variant.value, discount: {...variant.value.discount, value: item.toString()}}, valid: {...variant.valid, discount: {...variant.valid.discount, value: true}}})
  }

  const executeRemoveFile = (index: number) => {
    let tempPhotosValue = variant.value.photos.filter((_, temp_index) => (temp_index !== index))
    let tempPhotosValid = variant.valid.photos.filter((_, temp_index) => (temp_index !== index))
    let tempGalleryValid = (tempPhotosValid.length > 10) ? false : true

    if (tempGalleryValid) {
      legendValid(`${prefixLegend(component)}legend-variant-gallery-max`)
    } else {
      legendInvalid(`${prefixLegend(component)}legend-variant-gallery-max`)
    }

    setVariant({value: {...variant.value, photos: tempPhotosValue}, valid: {...variant.valid, photos: tempPhotosValid, gallery: tempGalleryValid}})
    cleanInputFile()
  }

  const validateDescriptionShortToVariant = () => {
    evaluateLegendObjectRequiredIcon(expressions.text, 'description_short', variant, setVariant, `${prefixLegend(component)}input-variant-description-short`, `${prefixLegend(component)}legend-variant-description-short-valid`, `${prefixLegend(component)}legend-variant-description-short-required`)
  }

  const validateDescriptionLongToVariant = () => {
    evaluateLegendObjectOptional(expressions.text, 'description_long', variant, setVariant, `${prefixLegend(component)}legend-variant-description-long-valid`)
  }

  const validateCodeToVariant = () => {
    evaluateLegendObjectOptionalIcon(expressions.code, 'code', variant, setVariant, `${prefixLegend(component)}input-variant-code`, `${prefixLegend(component)}legend-variant-code-valid`)
  }

  const validateSkuToVariant = () => {
    evaluateLegendObjectOptionalIcon(expressions.sku, 'sku', variant, setVariant, `${prefixLegend(component)}input-variant-sku`, `${prefixLegend(component)}legend-variant-sku-valid`)
  }

  const validateCodebarToVariant = () => {
    evaluateLegendObjectOptionalIcon(expressions.codebar, 'codebar', variant, setVariant, `${prefixLegend(component)}input-variant-codebar`, `${prefixLegend(component)}legend-variant-codebar-valid`)
  }

  const validatePriceSaleToVariant = () => {
    evaluateLegendObjectRequiredIcon(expressions.currency, 'price_sale', variant, setVariant, `${prefixLegend(component)}input-variant-price-sale`, `${prefixLegend(component)}legend-variant-price-sale-valid`, `${prefixLegend(component)}legend-variant-price-sale-required`)
  }

  const validatePricePurchaseToVariant = () => {
    evaluateLegendObjectRequiredIcon(expressions.currency, 'price_purchase', variant, setVariant, `${prefixLegend(component)}input-variant-price-purchase`, `${prefixLegend(component)}legend-variant-price-purchase-valid`, `${prefixLegend(component)}legend-variant-price-purchase-required`)
  }

  const validateDiscountValueToVariant = () => {
    evaluateLegendObjectCompositeRequiredIcon(expressions.currency, 'discount', 'value', variant, setVariant, `${prefixLegend(component)}input-variant-discount-value`, `${prefixLegend(component)}legend-variant-discount-value-valid`, `${prefixLegend(component)}legend-variant-discount-value-required`)
  }

  const validateDiscountQuantityToVariant = () => {
    evaluateLegendObjectCompositeRequiredIcon(expressions.quantity, 'discount', 'quantity', variant, setVariant, `${prefixLegend(component)}input-variant-discount-quantity`, `${prefixLegend(component)}legend-variant-discount-quantity-valid`, `${prefixLegend(component)}legend-variant-discount-quantity-required`)
  }

  function cleanInputFile() {
    Array.from(document.querySelectorAll(`input[name="${prefixLegend(component)}photos"]`)).forEach( (node)  => {
      let element = node as HTMLInputElement
      element.value = ''
    })
  }

  useEffect( () => {
    uploadTooltip()

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsForCompany])

  return (
    <>
      { companyForUser && settingsForCompany && variant.value && variant.valid &&
        <div className="form">
          <div className="mb-3">
            <label className="form-label required">{lang.labels.description}</label>
            <input id={`${prefixLegend(component)}input-variant-description-short`} className="form-control" type="text" autoComplete="off" name="description-short" value={variant.value.description_short} onChange={handleChangeDescriptionShortToVariant} onKeyUp={validateDescriptionShortToVariant} onBlur={validateDescriptionShortToVariant} />
            <AppLegend component={component} attribute={{check: variant.valid.description_short, name: "variant-description-short", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
          </div>
          <div className="mb-3">
            <label className="form-label">
              <div className="d-flex align-items-center">
                {lang.labels.comment}
                <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.additionalCommentDescriptionProductVariant}><FaExclamationCircle /></i>
              </div>
            </label>
            <TextareaAutosize className="form-control" name="description-long" minRows={2} maxRows={3} value={variant.value.description_long} onChange={handleChangeDescriptionLongToVariant} onKeyUp={validateDescriptionLongToVariant} onBlur={validateDescriptionLongToVariant} />
            <AppLegend component={component} attribute={{check: variant.valid.description_long, name: "variant-description-long", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
          </div>
          <div className="d-flex flex-wrap gap-3 mb-3">
            <div className="fv-row w-100 flex-md-root">
              <label className="form-label">
                <div className="d-flex align-items-center">
                  {lang.labels.code}
                  <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.uniqueProductCodeAssignedByCompany}><FaExclamationCircle /></i>
                </div>
              </label>
              <input id={`${prefixLegend(component)}input-variant-code`} className="form-control" type="text" autoComplete="off" name="code" value={variant.value.code} onChange={handleChangeCodeToVariant} onKeyUp={validateCodeToVariant} onBlur={validateCodeToVariant} />
              <AppLegend component={component} attribute={{check: variant.valid.code, name: "variant-code", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
            </div>
            <div className="fv-row w-100 flex-md-root">
              <label className="form-label">{lang.labels.sku}</label>
              <input id={`${prefixLegend(component)}input-variant-sku`} className="form-control" type="text" autoComplete="off" name="sku" value={variant.value.sku} onChange={handleChangeSkuToVariant} onKeyUp={validateSkuToVariant} onBlur={validateSkuToVariant} />
              <AppLegend component={component} attribute={{check: variant.valid.sku, name: "variant-sku", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
            </div>
            <div className="fv-row w-100 flex-md-root">
              <label className="form-label">{lang.labels.codebar}</label>
              <input id={`${prefixLegend(component)}input-variant-codebar`} className="form-control" type="text" autoComplete="off" name="codebar" value={variant.value.codebar} onChange={handleChangeCodebarToVariant} onKeyUp={validateCodebarToVariant} onBlur={validateCodebarToVariant} />
              <AppLegend component={component} attribute={{check: variant.valid.codebar, name: "variant-codebar", index: null, sub_index: null}} container={{valid: true, required: false, identical: false, exists: false}}></AppLegend>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label required">{lang.labels.unitMeasure}</label>
            <AppInputMeasureUnit component={component} options={{type: "product", main_modal: component ?? null, index: component?.split('-').pop() ?? null, sub_index: null}} companyForUser={companyForUser} settingsForCompany={settingsForCompany} variant={variant} setVariant={setVariant} loadSettingsForCompany={loadSettingsForCompany}></AppInputMeasureUnit>
          </div>
          <div className="fv-row mb-3">
            <div className="row row-cols-1 row-cols-md-2 g-3">
              <div className="col">
                <label className="form-label required">{lang.labels.priceSale}</label>
                <input id={`${prefixLegend(component)}input-variant-price-sale`} className="form-control" type="text" autoComplete="off" name="price" value={variant.value.price_sale} onChange={handleChangePriceSaleToVariant} onKeyUp={validatePriceSaleToVariant} onBlur={validatePriceSaleToVariant} />
                <AppLegend component={component} attribute={{check: variant.valid.price_sale, name: "variant-price-sale", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
              <div className="col">
                <div className="fv-row w-100 flex-md-root">
                  <label className="form-label required">{lang.labels.taxSale}</label>
                  <AppInputTaxFee component={component} options={{main_modal: component ?? null, index: component?.split('-').pop() ?? null, sub_index: null, type: "sale"}} companyForUser={companyForUser} settingsForCompany={settingsForCompany} variant={variant} setVariant={setVariant}></AppInputTaxFee>
                </div>
              </div>
            </div>
          </div>
          <div className="fv-row mb-3">
            <div className="row row-cols-1 row-cols-md-2 g-3">
              <div className="col">
                <label className="form-label required">{lang.labels.pricePurchase}</label>
                <input id={`${prefixLegend(component)}input-variant-price-purchase`} className="form-control" type="text" autoComplete="off" name="price" value={variant.value.price_purchase} onChange={handleChangePricePurchaseToVariant} onKeyUp={validatePricePurchaseToVariant} onBlur={validatePricePurchaseToVariant} />
                <AppLegend component={component} attribute={{check: variant.valid.price_purchase, name: "variant-price-purchase", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
              <div className="col">
                <div className="fv-row w-100 flex-md-root">
                  <label className="form-label required">{lang.labels.taxPurchase}</label>
                  <AppInputTaxFee component={component} options={{main_modal: component ?? null, index: component?.split('-').pop() ?? null, sub_index: null, type: "purchase"}} companyForUser={companyForUser} settingsForCompany={settingsForCompany} variant={variant} setVariant={setVariant}></AppInputTaxFee>
                </div>
              </div>
            </div>
          </div>
          <Container property={(variant.value.features.length > 0).toString()}>
            <label className="form-label required">{lang.labels.features}</label>
            { variant.value.features.map (( (item, index) => { return (
              <div key={index} className="row mb-3">
                <label className="col-lg-4 col-form-label d-flex align-items-center">
                  <span className="bullet bg-primary me-2"></span>
                  {item.name}
                </label>
                <div className="col-lg-8">
                  <AppInputFeatureOptions component={component} options={{index: index}} settingsForCompany={settingsForCompany} variant={variant} setVariant={setVariant}></AppInputFeatureOptions>
                </div>
              </div>
            )}))}
          </Container>
          <div className="mb-5">
            <label className="form-label">
              <div className="d-flex align-items-center">
                {lang.labels.discount}
                <i className="ms-2 fs-8" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.selectDiscountTypeAppliedToProduct}><FaExclamationCircle /></i>
              </div>
            </label>
            <div className="fv-row mb-5">
              <div className="row row-cols-1 row-cols-md-3 g-2">
                <div className="col">
                  <label className={`btn btn-outline btn-outline-dashed btn-active-light-primary d-flex text-start px-2 py-3 ${variant.value.discount.type === "none" && "active"}`}>
                    <span className="form-check form-check-custom form-check-solid form-check-sm align-items-center justify-content-center">
                      <input className="form-check-input" type="radio" value="none" checked={variant.value.discount.type === "none"} onChange={handleChangeDiscountTypeToVariant} />
                    </span>
                    <span className="ms-2 fs-6 fw-bold d-block text-nowrap">{lang.labels.noDiscount}</span>
                  </label>
                </div>
                <div className="col">
                  <label className={`btn btn-outline btn-outline-dashed btn-active-light-primary d-flex text-start px-2 py-3 ${variant.value.discount.type === "percentage" && "active"}`}>
                    <span className="form-check form-check-custom form-check-solid form-check-sm align-items-center justify-content-center">
                      <input className="form-check-input" type="radio" value="percentage" checked={variant.value.discount.type === "percentage"} onChange={handleChangeDiscountTypeToVariant} />
                    </span>
                    <span className="ms-2 fs-6 fw-bold d-block text-nowrap">{lang.labels.percentage}</span>
                  </label>
                </div>
                <div className="col">
                  <label className={`btn btn-outline btn-outline-dashed btn-active-light-primary d-flex text-start px-2 py-3 ${variant.value.discount.type === "fixed" && "active"}`}>
                    <span className="form-check form-check-custom form-check-solid form-check-sm align-items-center justify-content-center">
                      <input className="form-check-input" type="radio" value="fixed" checked={variant.value.discount.type === "fixed"}  onChange={handleChangeDiscountTypeToVariant} />
                    </span>
                    <span className="ms-2 fs-6 fw-bold d-block text-nowrap">{lang.labels.fixedAmount}</span>
                  </label>
                </div>
              </div>
            </div>
            <Container property={(variant.value.discount.type === "percentage").toString()}>
              <div className="mb-5">
                <label className="form-label required">{lang.labels.setDiscountPercentage}</label>
                <div className="d-flex flex-column text-center">
                  <div className="d-flex align-items-start justify-content-center text-nowrap">
                    <span className="fw-bolder fs-1">{variant.value.discount.value}</span>
                    <span className="fw-bold fs-7 ms-1 mt-1">%</span>
                  </div>
                  <Slider value={Number(variant.value.discount.value)} defaultValue={Number(variant.value.discount.value)} onChange={handleChangeSlideDiscountValueToVariant} onChangeComplete={handleChangeSlideDiscountValueToVariant} step={1} min={0} max={100} styles={{track: {height: 8, backgroundColor: "gray"}, rail: {height: 8, backgroundColor: "silver"}, handle: {height: 18, width: 18, opacity: 100, borderColor: "blue", backgroundColor: "black"}}} />
                </div>
              </div>
            </Container>
            <Container property={(variant.value.discount.type === "fixed").toString()}>
              <div className="mb-3">
                <label className="form-label required">{lang.labels.fixedDiscountedAmount}</label>
                <input id={`${prefixLegend(component)}input-variant-discount-value`} className="form-control" type="text" autoComplete="off" name="discount-value" value={variant.value.discount.value} onChange={handleChangeInputDiscountValueToVariant} onKeyUp={validateDiscountValueToVariant} onBlur={validateDiscountValueToVariant} />
                <AppLegend component={component} attribute={{check: variant.valid.discount.value, name: "variant-discount-value", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </Container>
            <Container property={(variant.value.discount.type === "percentage" || variant.value.discount.type === "fixed").toString()}>
              <div className="mb-0">
                <label className="form-label required">{lang.labels.quantityMinimumDiscount}</label>
                <input id={`${prefixLegend(component)}input-variant-discount-quantity`} className="form-control" type="text" autoComplete="off" name="discount-quantity" value={variant.value.discount.quantity} onChange={handleChangeInputDiscountQuantityToVariant} onKeyUp={validateDiscountQuantityToVariant} onBlur={validateDiscountQuantityToVariant} />
                <AppLegend component={component} attribute={{check: variant.valid.discount.quantity, name: "variant-discount-quantity", index: null, sub_index: null}} container={{valid: true, required: true, identical: false, exists: false}}></AppLegend>
              </div>
            </Container>
          </div>
          <div className="mb-0">
            <div className="dropzone dz-clickable position-relative">
              <input className="position-absolute opacity-0 w-100 h-100 top-0 bottom-0 start-0 end-0 cursor-pointer" type="file" name={`${prefixLegend(component)}photos`} accept=".png, .jpg, .jpeg" multiple onChange={handleChangeFiles} />
              { variant.value.photos.length > 0 && variant.valid.photos.length > 0 && variant.value.photos.length === variant.valid.photos.length
                ?
                <>
                  { variant.value.photos.map (( (item, index) => { return (
                    <div key={index} className="dz-preview dz-file-preview dz-error dz-complete">
                      <div className="dz-image">
                        <img src={variant.valid.photos[index].file ? item.view : imgDefault} className="w-100 h-100" alt={lang.labels.photography + index} />
                      </div>
                      <div className="dz-details">
                        <div className="dz-size">
                          <span>{item.file.size ? (item.file.size / (1024 * 1024)).toFixed(2) : item.file.length} {lang.labels.mb}</span>
                        </div>
                        <div className="dz-filename">
                          <span className="text-uppercase">{item.file.name ? item.file.name.split('.').pop() : item.file.split(".").pop()}</span>
                        </div>
                      </div>
                      { variant.valid.photos[index].file
                        ?
                        <div className="dz-success-mark">
                          <svg viewBox="0 0 55 55" version="1.1" xmlns="http://www.w3.org/2000/svg">
                            <title>{lang.labels.success}</title>
                            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                              <path d="M23.5,31.8431458 L17.5852419,25.9283877 C16.0248253,24.3679711 13.4910294,24.366835 11.9289322,25.9289322 C10.3700136,27.4878508 10.3665912,30.0234455 11.9283877,31.5852419 L20.4147581,40.0716123 C20.5133999,40.1702541 20.6159315,40.2626649 20.7218615,40.3488435 C22.2835669,41.8725651 24.794234,41.8626202 26.3461564,40.3106978 L43.3106978,23.3461564 C44.8771021,21.7797521 44.8758057,19.2483887 43.3137085,17.6862915 C41.7547899,16.1273729 39.2176035,16.1255422 37.6538436,17.6893022 L23.5,31.8431458 Z M27,53 C41.3594035,53 53,41.3594035 53,27 C53,12.6405965 41.3594035,1 27,1 C12.6405965,1 1,12.6405965 1,27 C1,41.3594035 12.6405965,53 27,53 Z" strokeOpacity="0.198794158" stroke="#747474" fillOpacity="0.816519475" fill="#FFFFFF"></path>
                            </g>
                          </svg>
                        </div>
                        :
                        <div className="dz-error-mark">
                          <svg viewBox="0 0 55 55" version="1.1" xmlns="http://www.w3.org/2000/svg">
                            <title>{lang.labels.error}</title>
                            <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                              <g stroke="#7E8299" strokeOpacity="0.198794158" fill="#a92222" fillOpacity="0.816519475">
                                <path d="M32.6568542,29 L38.3106978,23.3461564 C39.8771021,21.7797521 39.8758057,19.2483887 38.3137085,17.6862915 C36.7547899,16.1273729 34.2176035,16.1255422 32.6538436,17.6893022 L27,23.3431458 L21.3461564,17.6893022 C19.7823965,16.1255422 17.2452101,16.1273729 15.6862915,17.6862915 C14.1241943,19.2483887 14.1228979,21.7797521 15.6893022,23.3461564 L21.3431458,29 L15.6893022,34.6538436 C14.1228979,36.2202479 14.1241943,38.7516113 15.6862915,40.3137085 C17.2452101,41.8726271 19.7823965,41.8744578 21.3461564,40.3106978 L27,34.6568542 L32.6538436,40.3106978 C34.2176035,41.8744578 36.7547899,41.8726271 38.3137085,40.3137085 C39.8758057,38.7516113 39.8771021,36.2202479 38.3106978,34.6538436 L32.6568542,29 Z M27,53 C41.3594035,53 53,41.3594035 53,27 C53,12.6405965 41.3594035,1 27,1 C12.6405965,1 1,12.6405965 1,27 C1,41.3594035 12.6405965,53 27,53 Z"></path>
                              </g>
                            </g>
                          </svg>
                        </div>
                      }
                      { !variant.valid.photos[index].file &&
                        <div className="dz-error-message">
                          <span className="text-justify">{variant.valid.photos[index].message}</span>
                        </div>
                      }
                      <button className="dz-remove" type="button" onClick={() => executeRemoveFile(index)}>{lang.labels.remove}</button>
                    </div>
                  )}))}
                </>
                :
                <div className="dz-message">
                  <span className="svg-icon svg-icon-primary svg-icon-3x">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path opacity="0.3" d="M5 16C3.3 16 2 14.7 2 13C2 11.3 3.3 10 5 10H5.1C5 9.7 5 9.3 5 9C5 6.2 7.2 4 10 4C11.9 4 13.5 5 14.3 6.5C14.8 6.2 15.4 6 16 6C17.7 6 19 7.3 19 9C19 9.4 18.9 9.7 18.8 10C18.9 10 18.9 10 19 10C20.7 10 22 11.3 22 13C22 14.7 20.7 16 19 16H5ZM8 13.6H16L12.7 10.3C12.3 9.89999 11.7 9.89999 11.3 10.3L8 13.6Z" fill="black"/>
                      <path d="M11 13.6V19C11 19.6 11.4 20 12 20C12.6 20 13 19.6 13 19V13.6H11Z" fill="black"/>
                    </svg>
                  </span>
                  <div className="d-grid ms-5">
                    <span className="fs-6 fw-bold">{lang.labels.dropFilesHereOrClickUpload}</span>
                    <span className="fw-semibold text-muted">{lang.labels.uploadUpToFiles}</span>
                  </div>
                </div>
              }
            </div>
            <div className="form-text text-justify">{lang.labels.setProductMediaGallery}</div>
            <div className={`fv-plugins-message-container invalid-feedback ${variant.valid.gallery && "d-none"}`}>
              <div data-field="document" data-validator="file">{lang.labels.limitFilesMaximum}</div>
            </div>
          </div>
        </div>
      }
    </>
  )
};

export default AppProductVariantForm;
